import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Dumbbell, Lock, Loader2, Check, Mail, Flame, StretchHorizontal, PlayCircle, HeartPulse, Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API_BASE } from "@/lib/api";

const GOALS = [
  { id: "lose_weight", name: "Lose Weight" },
  { id: "build_muscle", name: "Build Muscle" },
  { id: "endurance", name: "Endurance" },
  { id: "general", name: "General Fitness" },
  { id: "vo2max", name: "VO2 Max Training" },
  { id: "trek", name: "Upcoming Trek" },
];
const TREKS = {
  ebc: "Everest Base Camp",
  ladakh_marathon: "Ladakh Marathon",
  valley_of_flowers: "Valley of Flowers Trek",
  sandakphu: "Sandakphu Trek",
  chadar: "Chadar Frozen River Trek",
  himalayan: "Himalayan Trek",
};
const GENDERS = [
  { id: "male", name: "Male" },
  { id: "female", name: "Female" },
  { id: "other", name: "Prefer not to say" },
];
const EQUIPMENT = [
  { id: "none", name: "No Equipment" },
  { id: "bands", name: "Resistance Bands" },
  { id: "dumbbells", name: "Dumbbells" },
  { id: "kettlebell", name: "Kettlebell" },
  { id: "trx", name: "TRX" },
  { id: "boxing_bag", name: "Boxing Bag" },
  { id: "yoga_mat", name: "Yoga Mat" },
  { id: "spin_studio", name: "Spin Studio" },
  { id: "aerobics_studio", name: "Aerobics/Dance Studio" },
  { id: "full_gym", name: "Full Gym" },
];
const HEALTH_CONDITIONS = ["Diabetic", "PCOS", "PCOD", "Any Heart Condition", "Other"];
const EXPERIENCE = [
  { id: "beginner", name: "Beginner" },
  { id: "intermediate", name: "Intermediate" },
  { id: "advanced", name: "Advanced" },
];
const ZONE_META = {
  1: { color: "#9DB8CB", label: "Recovery" },
  2: { color: "#89937C", label: "Fat Burn" },
  3: { color: "#E3B23C", label: "Aerobic" },
  4: { color: "#D97B4F", label: "Threshold" },
  5: { color: "#B8433A", label: "Max" },
};

const HRZoneBar = ({ zones, hr }) => {
  if (!zones || !hr) return null;
  const lo = hr.bands[String(zones[0])][0];
  const hi = hr.bands[String(zones[zones.length - 1])][1];
  return (
    <div data-testid="hr-zone-bar" className="mb-4">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#89937C] font-semibold mb-2">
        <HeartPulse size={13} /> Target Heart Rate Zone
      </div>
      <div className="flex gap-1 items-end">
        {[1, 2, 3, 4, 5].map((z) => {
          const active = zones.includes(z);
          const band = hr.bands[String(z)];
          return (
            <div
              key={z}
              className="flex-1 rounded-lg px-1 py-1.5 text-center"
              style={{ backgroundColor: ZONE_META[z].color, opacity: active ? 1 : 0.18 }}
            >
              <div className="text-[10px] font-bold text-white leading-none">Z{z}</div>
              <div className="text-[9px] text-white/90 leading-tight mt-0.5">{band[0]}–{band[1]}</div>
            </div>
          );
        })}
      </div>
      <p className="mt-1.5 text-xs text-[#5C665A]">
        Train at <span className="font-medium text-[#2D372B]">Zone {zones.join("–")} · {lo}–{hi} bpm</span>
        {" "}({ZONE_META[zones[zones.length - 1]].label}) — max HR ≈ {hr.max_hr} bpm
      </p>
    </div>
  );
};

const bmiTone = (cat) =>
  cat === "Normal" ? "bg-[#89937C]/15 text-[#2D372B]" : "bg-[#AD6A48]/15 text-[#8A5236]";

const ytLink = (name) =>
  "https://www.youtube.com/results?search_query=" +
  encodeURIComponent(name.split("—")[0].replace(/\(.*?\)/g, "").trim() + " proper form tutorial");

const ExRow = ({ name, right }) => (
  <li className="flex justify-between items-start gap-3 text-sm">
    <a
      href={ytLink(name)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="exercise-video-link"
      className="group inline-flex items-center gap-1.5 text-[#2D372B] hover:text-[#AD6A48] transition-colors"
      title="Watch proper form on YouTube"
    >
      <PlayCircle size={14} className="text-[#AD6A48]/70 group-hover:text-[#AD6A48] shrink-0" />
      <span className="underline decoration-dotted decoration-[#AD6A48]/40 underline-offset-2">{name}</span>
    </a>
    {right && <span className="text-[#5C665A] whitespace-nowrap text-right">{right}</span>}
  </li>
);

function DayCard({ day, hr }) {
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-2xl p-6">
      <h2 className="font-serif-display text-2xl text-[#2D372B] mb-4">{day.title}</h2>
      <HRZoneBar zones={day.zones} hr={hr} />
      <div className="mb-4">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#89937C] font-semibold mb-2">
          <StretchHorizontal size={13} /> Mobility Work
        </div>
        <ul className="space-y-1.5 text-sm text-[#5C665A]">
          {(day.mobility || []).map((m) => <ExRow key={m} name={m} />)}
        </ul>
      </div>
      <div className="mb-4">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#89937C] font-semibold mb-2">
          <Dumbbell size={13} /> Workout of the Day
        </div>
        {day.format && (
          <p className="text-xs text-[#AD6A48] font-medium mb-2">{day.format}</p>
        )}
        <ul className="space-y-2">
          {day.exercises.map((ex) => <ExRow key={ex.name} name={ex.name} right={ex.scheme} />)}
        </ul>
      </div>
      {day.finisher && (
        <div className="pt-3 border-t border-[#E5E2D9]">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#AD6A48] font-semibold mb-1">
            <Flame size={13} /> Finisher
          </div>
          <p className="text-sm text-[#5C665A]">{day.finisher}</p>
        </div>
      )}
    </div>
  );
}

export default function WorkoutPlan() {
  const [params] = useSearchParams();
  const trekTarget = TREKS[params.get("trek")] ? params.get("trek") : null;
  const [goal, setGoal] = useState(trekTarget ? "trek" : "general");
  const [gender, setGender] = useState(null);
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [conditions, setConditions] = useState([]);
  const [healthNotes, setHealthNotes] = useState("");
  const [age, setAge] = useState("");
  const [experience, setExperience] = useState(null);
  const [hr, setHr] = useState(null);
  const [weight, setWeight] = useState("");
  const [unit, setUnit] = useState("kg");
  const [height, setHeight] = useState("");
  const [equipment, setEquipment] = useState(["none"]);
  const [loading, setLoading] = useState(false);
  const [teaser, setTeaser] = useState(null);
  const [goalLabel, setGoalLabel] = useState(null);
  const [bmi, setBmi] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [email, setEmail] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [plan, setPlan] = useState(null);

  const weightKg = useMemo(() => {
    const w = parseFloat(weight);
    if (!w) return null;
    return unit === "lbs" ? w * 0.453592 : w;
  }, [weight, unit]);

  const liveBmi = useMemo(() => {
    const h = parseFloat(height);
    if (!weightKg || !h || h < 100 || h > 250) return null;
    const v = weightKg / Math.pow(h / 100, 2);
    const value = Math.round(v * 10) / 10;
    const category = value < 18.5 ? "Underweight" : value < 25 ? "Normal" : value < 30 ? "Overweight" : "Obese";
    return { value, category };
  }, [weightKg, height]);

  const toggleEquip = (id) =>
    setEquipment((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));

  const toggleCondition = (c) =>
    setConditions((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));

  const generate = async () => {
    if (!gender) return toast.error("Please select your gender.");
    const a = parseInt(age, 10);
    if (!a || a < 10 || a > 90) return toast.error("Please enter your age (10–90).");
    if (!weightKg || weightKg < 20 || weightKg > 350) return toast.error(`Please enter your weight in ${unit}.`);
    const h = parseFloat(height);
    if (!h || h < 100 || h > 250) return toast.error("Please enter your height in cm (100–250).");
    if (equipment.length === 0) return toast.error("Select at least one equipment option.");
    if (!experience) return toast.error("Please select your experience level.");
    if (!country.trim()) return toast.error("Please enter your country.");
    setLoading(true);
    try {
      const r = await axios.post(`${API_BASE}/workout/plan`, {
        goal,
        trek_target: goal === "trek" ? trekTarget : null,
        gender,
        age: parseInt(age, 10),
        experience,
        country,
        phone: phone.trim() || null,
        health_conditions: conditions,
        health_notes: healthNotes.trim() || null,
        weight_kg: Math.round(weightKg * 10) / 10,
        height_cm: h,
        equipment,
      });
      setTeaser(r.data.teaser);
      setGoalLabel(r.data.goal || null);
      setBmi(r.data.bmi);
      setHr(r.data.hr);
      setPlanId(r.data.plan_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate plan.");
    } finally {
      setLoading(false);
    }
  };

  const unlock = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Please enter a valid email.");
    setUnlocking(true);
    try {
      const r = await axios.post(`${API_BASE}/workout/unlock`, { plan_id: planId, email });
      setPlan(r.data.plan);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not unlock plan.");
    } finally {
      setUnlocking(false);
    }
  };

  const chip = (active) =>
    `px-4 py-2.5 rounded-full text-sm font-medium border transition-colors cursor-pointer ${
      active ? "bg-[#2D372B] text-[#F7F5F0] border-[#2D372B]" : "bg-white text-[#5C665A] border-[#E5E2D9] hover:border-[#89937C]"
    }`;

  const BmiBadge = ({ data }) =>
    data ? (
      <div data-testid="workout-bmi-badge" className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full font-medium ${bmiTone(data.category)}`}>
        BMI {data.value} · {data.category}
      </div>
    ) : null;

  return (
    <div className="min-h-screen bg-[#F7F5F0] px-6 py-10">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B] mb-8">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-[#89937C]/15 flex items-center justify-center">
            <Dumbbell size={18} className="text-[#89937C]" />
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2D372B]">Free Workout Plan</h1>
        </div>
        <p className="text-[#5C665A] mb-10">Answer a few quick questions — get a personalised 7-day schedule built by Coach Sanjay.</p>

        {plan ? (
          <div data-testid="workout-full-plan" className="print-area space-y-6">
            <div className="hidden print:block mb-2">
              <h2 className="font-serif-display text-3xl text-[#2D372B]">Your 7-Day {plan.goal} Plan</h2>
              <p className="text-sm text-[#5C665A]">Coach Sanjay · The Jay Project · thejayproject.app</p>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="no-print flex items-center gap-2 text-sm text-[#89937C] font-medium print:hidden">
                <Check size={16} /> Plan unlocked — a copy is on its way to {email}
              </div>
              <div className="flex items-center gap-3">
                <BmiBadge data={bmi} />
                <button
                  data-testid="workout-print-btn"
                  onClick={() => window.print()}
                  className="no-print inline-flex items-center gap-2 rounded-full px-5 py-2.5 border border-[#2D372B]/25 text-sm font-medium text-[#2D372B] hover:bg-[#2D372B] hover:text-[#F7F5F0] transition-colors print:hidden"
                >
                  <Printer size={14} /> Print plan
                </button>
              </div>
            </div>
            <p className="no-print text-xs text-[#5C665A] flex items-center gap-1.5 print:hidden">
              <PlayCircle size={13} className="text-[#AD6A48]" /> Tap any move to watch a proper-form video on YouTube.
            </p>
            {plan.days.map((d) => <DayCard key={d.title} day={d} hr={plan.hr || hr} />)}
            <div className="bg-[#2D372B] text-[#F7F5F0] rounded-2xl p-6 print:bg-white print:text-[#2D372B] print:border print:border-[#E5E2D9]">
              <h3 className="font-serif-display text-xl mb-3">Coach's Tips</h3>
              <ul className="space-y-1.5 text-sm opacity-90">
                {plan.tips.map((t) => <li key={t}>• {t}</li>)}
              </ul>
            </div>
            <Link to="/#bootcamps" className="no-print block text-center rounded-full px-6 py-3.5 bg-[#89937C] text-white font-medium hover:bg-[#737D67] transition-colors print:hidden">
              Want real results faster? Join a bootcamp →
            </Link>
          </div>
        ) : teaser ? (
          <div data-testid="workout-teaser" className="space-y-5">
            {goalLabel && (
              <div data-testid="teaser-goal-label" className="inline-flex items-center gap-2 rounded-full border border-[#C57B57]/40 bg-[#C57B57]/10 px-4 py-2 text-sm font-medium text-[#AD6A48]">
                <Flame size={14} /> Your 7-Day {goalLabel} Plan
              </div>
            )}
            {bmi && (
              <div className="flex items-center gap-3">
                <BmiBadge data={bmi} />
                <span className="text-xs text-[#5C665A]">Your plan is calibrated to this.</span>
              </div>
            )}
            <div className="bg-white border border-[#E5E2D9] rounded-2xl p-6">
              <h2 className="font-serif-display text-2xl text-[#2D372B] mb-4">{teaser.title}</h2>
              <HRZoneBar zones={teaser.zones} hr={hr} />
              <div className="mb-4">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#89937C] font-semibold mb-2">
                  <StretchHorizontal size={13} /> Mobility Work
                </div>
                <ul className="space-y-1.5 text-sm text-[#5C665A]">
                  {(teaser.mobility || []).map((m) => <ExRow key={m} name={m} />)}
                </ul>
              </div>
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#89937C] font-semibold mb-2">
                <Dumbbell size={13} /> Workout of the Day
              </div>
              {teaser.format && <p className="text-xs text-[#AD6A48] font-medium mb-2">{teaser.format}</p>}
              <ul className="space-y-2">
                {teaser.exercises.map((ex) => <ExRow key={ex.name} name={ex.name} right={ex.scheme} />)}
                <li className="text-sm text-[#5C665A] italic pt-2">+ more exercises & the finisher for this day…</li>
              </ul>
            </div>
            <div className="bg-white border-2 border-dashed border-[#89937C]/50 rounded-2xl p-6 text-center">
              <Lock size={20} className="mx-auto text-[#89937C] mb-2" />
              <h3 className="font-serif-display text-xl text-[#2D372B] mb-1">Unlock your full 7-day plan</h3>
              <p className="text-sm text-[#5C665A] mb-4">Enter your email — I'll show the full plan here and email you a copy.</p>
              <div className="flex gap-2 max-w-sm mx-auto">
                <Input
                  data-testid="workout-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="bg-[#F7F5F0] border-[#E5E2D9] h-12"
                />
                <button
                  data-testid="workout-unlock-btn"
                  onClick={unlock}
                  disabled={unlocking}
                  className="inline-flex items-center gap-2 rounded-full px-6 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60"
                >
                  {unlocking ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                  Unlock
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {trekTarget && (
              <div data-testid="trek-target-banner" className="flex items-center gap-3 rounded-2xl border border-[#C57B57]/40 bg-[#C57B57]/10 px-5 py-4">
                <Flame size={18} className="text-[#AD6A48] shrink-0" />
                <div className="text-sm text-[#2D372B]">
                  <span className="font-semibold">Trek-prep mode: {TREKS[trekTarget]}.</span>{" "}
                  Your plan's finishers, tips and zones will be tuned for this goal. Change the goal below to switch off.
                </div>
              </div>
            )}
            <div>
              <div className="overline mb-3">1. Your goal</div>
              <div className="flex flex-wrap gap-2">
                {GOALS.map((g) => (
                  <button key={g.id} data-testid={`goal-${g.id}`} onClick={() => setGoal(g.id)} className={chip(goal === g.id)}>
                    {g.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="overline mb-3">2. Gender</div>
              <div className="flex flex-wrap gap-2">
                {GENDERS.map((g) => (
                  <button key={g.id} data-testid={`gender-${g.id}`} onClick={() => setGender(g.id)} className={chip(gender === g.id)}>
                    {g.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="overline mb-3">3. Age</div>
              <Input
                data-testid="workout-age-input"
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="e.g. 35"
                className="bg-white border-[#E5E2D9] h-12 max-w-[160px]"
                inputMode="numeric"
              />
            </div>
            <div>
              <div className="overline mb-3">4. Current weight</div>
              <div className="flex items-center gap-3">
                <Input
                  data-testid="workout-weight-input"
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={unit === "kg" ? "e.g. 78" : "e.g. 172"}
                  className="bg-white border-[#E5E2D9] h-12 max-w-[160px]"
                  inputMode="decimal"
                />
                <div className="flex rounded-full border border-[#E5E2D9] bg-white p-1">
                  {["kg", "lbs"].map((u) => (
                    <button
                      key={u}
                      data-testid={`weight-unit-${u}`}
                      onClick={() => setUnit(u)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        unit === u ? "bg-[#2D372B] text-[#F7F5F0]" : "text-[#5C665A]"
                      }`}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <div className="overline mb-3">5. Height (cm)</div>
              <Input
                data-testid="workout-height-input"
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="e.g. 175"
                className="bg-white border-[#E5E2D9] h-12 max-w-[160px]"
                inputMode="decimal"
              />
              {liveBmi && (
                <div className="mt-3">
                  <BmiBadge data={liveBmi} />
                </div>
              )}
            </div>
            <div>
              <div className="overline mb-3">6. Available equipment</div>
              <div className="flex flex-wrap gap-2">
                {EQUIPMENT.map((eq) => (
                  <button key={eq.id} data-testid={`equip-${eq.id}`} onClick={() => toggleEquip(eq.id)} className={chip(equipment.includes(eq.id))}>
                    {eq.name}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-[#5C665A]">Select everything you have — your workouts are built around it.</p>
            </div>
            <div>
              <div className="overline mb-3">7. Experience level</div>
              <div className="flex flex-wrap gap-2">
                {EXPERIENCE.map((x) => (
                  <button key={x.id} data-testid={`experience-${x.id}`} onClick={() => setExperience(x.id)} className={chip(experience === x.id)}>
                    {x.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="overline mb-3">8. Current health condition</div>
              <div className="flex flex-wrap gap-2">
                {HEALTH_CONDITIONS.map((c) => (
                  <button
                    key={c}
                    data-testid={`health-${c.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => toggleCondition(c)}
                    className={chip(conditions.includes(c))}
                  >
                    {c}
                  </button>
                ))}
              </div>
              {conditions.length > 0 && (
                <>
                  <textarea
                    data-testid="health-notes-input"
                    value={healthNotes}
                    onChange={(e) => setHealthNotes(e.target.value)}
                    placeholder="Tell us more — condition details, medication, anything we should know…"
                    rows={3}
                    className="mt-3 w-full rounded-2xl border border-[#E5E2D9] bg-white px-4 py-3 text-sm text-[#2D372B] placeholder:text-[#5C665A]/60 focus:outline-none focus:ring-2 focus:ring-[#89937C]/40"
                  />
                  <p data-testid="health-disclaimer" className="mt-2 text-xs text-[#AD6A48] font-medium">
                    ⚠ These conditions need special attention — please get your doctor's approval before following this program.
                  </p>
                </>
              )}
            </div>
            <div>
              <div className="overline mb-3">9. Country</div>
              <Input
                data-testid="workout-country-input"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="e.g. India"
                className="bg-white border-[#E5E2D9] h-12 max-w-[280px]"
              />
            </div>
            <div>
              <div className="overline mb-3">10. Contact number <span className="normal-case opacity-60">(optional)</span></div>
              <Input
                data-testid="workout-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. +91 98XXXXXX00"
                className="bg-white border-[#E5E2D9] h-12 max-w-[280px]"
                inputMode="tel"
              />
            </div>
            <button
              data-testid="workout-generate-btn"
              onClick={generate}
              disabled={loading}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full px-10 py-4 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Generate my plan
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
